import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import 'flag-icons/css/flag-icons.min.css';

function Streamer() {
  const { t } = useLanguage();
  const [streamers, setStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Default image URL
  const defaultImage = "https://res.cloudinary.com/dez2cotie/image/upload/v1751233643/pngegg_pk3dvl.png";

  // Country mapping to get country code from country name
  const countryMapping = {
    'World': 'W',
    'world': 'W',
    'France': 'FR',
    'france': 'FR',
    'España': 'ES',
    'Spain': 'ES',
    'spain': 'ES',
    'United States': 'US',
    'united states': 'US',
    'USA': 'US',
    'usa': 'US',
    'United Kingdom': 'GB',
    'united kingdom': 'GB',
    'UK': 'GB',
    'uk': 'GB',
    'Deutschland': 'DE',
    'deutschland': 'DE',
    'Germany': 'DE',
    'germany': 'DE',
    'Italia': 'IT',
    'italia': 'IT',
    'Italy': 'IT',
    'italy': 'IT',
    'Portugal': 'PT',
    'portugal': 'PT',
    'Canada': 'CA',
    'canada': 'CA',
    'Australia': 'AU',
    'australia': 'AU',
    'Brasil': 'BR',
    'brasil': 'BR',
    'Brazil': 'BR',
    'brazil': 'BR',
    'México': 'MX',
    'mexico': 'MX',
    'Mexico': 'MX',
    'Argentina': 'AR',
    'argentina': 'AR',
    'Colombia': 'CO',
    'colombia': 'CO',
    'Chile': 'CL',
    'chile': 'CL',
    'Perú': 'PE',
    'peru': 'PE',
    'Peru': 'PE',
    '日本': 'JP',
    'Japan': 'JP',
    'japan': 'JP',
    '한국': 'KR',
    'Korea': 'KR',
    'korea': 'KR',
    'South Korea': 'KR',
    'south korea': 'KR',
    '中国': 'CN',
    'China': 'CN',
    'china': 'CN',
    'India': 'IN',
    'india': 'IN',
    'Россия': 'RU',
    'Russia': 'RU',
    'russia': 'RU',
    'Nederland': 'NL',
    'netherlands': 'NL',
    'Netherlands': 'NL',
    'België': 'BE',
    'belgium': 'BE',
    'Belgium': 'BE',
    'Schweiz': 'CH',
    'switzerland': 'CH',
    'Switzerland': 'CH',
    'Österreich': 'AT',
    'austria': 'AT',
    'Austria': 'AT',
    'Philippines': 'PH',
    'philippines': 'PH'
  };

  // Get country code from country name
  const getCountryCode = (countryName) => {
    if (!countryName) return null;
    const trimmedName = countryName.trim();
    return countryMapping[trimmedName] || 
           countryMapping[trimmedName.toLowerCase()] || 
           countryMapping[trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1).toLowerCase()];
  };

  // Get flag CSS class for flag-icons
  const getFlagClass = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) return null;
    return `fi fi-${countryCode.toLowerCase()}`;
  };

  // Get flag display with both class and code
  const getFlagDisplay = (countryName) => {
    const countryCode = getCountryCode(countryName);
    if (countryCode) {
      const flagClass = getFlagClass(countryCode);
      return { flagClass, countryCode };
    }
    return { flagClass: null, countryCode: null };
  };

  // Social media platforms configuration
  const socialPlatforms = [
    { name: 'YouTube', icon: '▶️', color: 'text-red-500', urlPattern: 'https://www.youtube.com/@', useAt: true },
    { name: 'TikTok', icon: '🎵', color: 'text-gray-900', urlPattern: 'https://www.tiktok.com/@', useAt: true },
    { name: 'Instagram', icon: '📷', color: 'text-pink-500', urlPattern: 'https://www.instagram.com/', useAt: false },
    { name: 'Twitter', icon: '🐦', color: 'text-blue-400', urlPattern: 'https://twitter.com/', useAt: false },
    { name: 'Facebook', icon: '👤', color: 'text-blue-600', urlPattern: 'https://www.facebook.com/', useAt: false },
    { name: 'Twitch', icon: '🎮', color: 'text-purple-500', urlPattern: 'https://www.twitch.tv/', useAt: false },
    { name: 'Discord', icon: '💬', color: 'text-indigo-500', urlPattern: 'https://discord.com/users/', useAt: false }
  ];

  // Parse social media from string
  const parseSocialMedia = (socialString) => {
    if (!socialString) return [];
    
    try {
      // Handle JSON array format
      if (socialString.startsWith('[')) {
        const urls = JSON.parse(socialString);
        return urls.map(url => {
          for (const platform of socialPlatforms) {
            if (url.includes(platform.urlPattern)) {
              const username = url.replace(platform.urlPattern, '');
              return {
                platform: platform.name,
                url: url,
                username: username,
                icon: platform.icon,
                color: platform.color,
                useAt: platform.useAt
              };
            }
          }
          return null;
        }).filter(Boolean);
      }
      
      // Handle comma-separated string format
      const urls = socialString.split(',').map(url => url.trim()).filter(Boolean);
      return urls.map(url => {
        for (const platform of socialPlatforms) {
          if (url.includes(platform.urlPattern)) {
            const username = url.replace(platform.urlPattern, '');
            return {
              platform: platform.name,
              url: url,
              username: username,
              icon: platform.icon,
              color: platform.color,
              useAt: platform.useAt
            };
          }
        }
        return null;
      }).filter(Boolean);
    } catch (error) {
      console.error('Error parsing social media:', error);
      return [];
    }
  };

  // Fetch all streamers
  useEffect(() => {
    const fetchStreamers = async () => {
      try {
        setLoading(true);
        const response = await fetch('/.netlify/functions/get-admin-ambassadors');
        const result = await response.json();
        
        if (result.success && result.data) {
          setStreamers(result.data);
        }
      } catch (error) {
        console.error('Error fetching streamers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStreamers();
  }, []);

  // Filter streamers based on search query
  const filteredStreamers = streamers.filter(streamer => {
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase().trim();
    return (
      streamer.owner?.toLowerCase().includes(searchLower) ||
      streamer.name?.toLowerCase().includes(searchLower) ||
      streamer.country?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-black bg-opacity-75 text-white pt-32 pb-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">
            {t('dropZone')}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover all the amazing creators and their Drop Zones
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <input
            type="text"
            placeholder={t('searchStreamers')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-600 border-t-pink-500"></div>
          </div>
        ) : (
          <>
            {/* Streamers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredStreamers.map((streamer) => {
                // Get image using same logic as Home.js
                let imageUrl = defaultImage;
                
                try {
                  if (streamer.image) {
                    if (typeof streamer.image === 'string' && streamer.image.startsWith('{')) {
                      const imageData = JSON.parse(streamer.image);
                      if (imageData.logo && Array.isArray(imageData.logo) && imageData.logo.length > 0) {
                        imageUrl = imageData.logo[0];
                      } else if (imageData.profile && Array.isArray(imageData.profile) && imageData.profile.length > 0) {
                        imageUrl = imageData.profile[0];
                      }
                    }
                    else if (typeof streamer.image === 'string' && streamer.image.startsWith('[')) {
                      const imageUrls = JSON.parse(streamer.image);
                      if (imageUrls.length > 0) {
                        imageUrl = imageUrls[0];
                      }
                    }
                    else if (Array.isArray(streamer.image) && streamer.image.length > 0) {
                      imageUrl = streamer.image[0];
                    }
                    else if (typeof streamer.image === 'string' && streamer.image.length > 0) {
                      imageUrl = streamer.image;
                    }
                  }
                } catch (error) {
                  console.error('Error parsing streamer image:', error);
                }

                // Parse social media
                const socialLinks = parseSocialMedia(streamer.social);

                return (
                  <div
                    key={streamer.id}
                    className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-cyan-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20"
                  >
                    {/* Streamer Image */}
                    <a href={`/${streamer.owner}`} className="block">
                      <div className="relative aspect-square overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={streamer.owner}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                          onError={(e) => {
                            e.target.src = defaultImage;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60"></div>
                      </div>
                    </a>
                    
                    {/* Card Content */}
                    <div className="p-6">
                      {/* Streamer Name */}
                      <a href={`/${streamer.owner}`}>
                        <h3 className="text-xl font-bold text-center text-white mb-3 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-pink-500 hover:to-cyan-500 transition-all duration-300">
                          {streamer.owner?.toUpperCase()}
                        </h3>
                      </a>
                      
                      {/* Country */}
                      {streamer.country && (
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-4 pb-4 border-b border-gray-700">
                          {(() => {
                            const { flagClass, countryCode } = getFlagDisplay(streamer.country);
                            if (countryCode === 'W') {
                              return <span style={{ fontSize: '16px' }}>🌍</span>;
                            } else if (flagClass) {
                              return <span className={flagClass} style={{ fontSize: '16px', width: '24px', height: '18px', borderRadius: '2px', display: 'inline-block' }}></span>;
                            } else {
                              return <span>🇫🇷</span>;
                            }
                          })()}
                          <span>{streamer.country}</span>
                        </div>
                      )}
                      
                      {/* Social Media Links */}
                      {socialLinks.length > 0 && (
                        <div className="space-y-2">
                          {socialLinks.map((social, index) => (
                            <a
                              key={index}
                              href={social.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors p-2 rounded hover:bg-gray-700"
                            >
                              <span className="text-lg">{social.icon}</span>
                              <span className="font-medium">{social.platform}:</span>
                              <span className={`${social.color} truncate`}>
                                {social.useAt ? '@' : ''}{social.username}
                              </span>
                            </a>
                          ))}
                        </div>
                      )}
                      
                      {/* Visit Drop Zone Button */}
                      <a
                        href={`/${streamer.owner}`}
                        className="block mt-4 text-center bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-700 hover:to-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300"
                      >
                        Visit Drop Zone
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* No Results */}
            {filteredStreamers.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400 text-xl">
                  {searchQuery ? `No streamers found for "${searchQuery}"` : 'No streamers available'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Streamer;

